import { AppFactory } from 'vue-infra';

import BaseService from '../../common/BaseService';
import WorkatoClient from '../../clients/WorkatoClient';
import workatoTransformer from './workatoTransformer';

const store = AppFactory.store;

class WorkatoService extends BaseService {
  constructor() {
    super({ name: 'WorkatoService' });
    this.workatoTokenClient = null;
    this.workatoRecipeClient = null;
    this.workatoAdminsClient = null;
    this.existingWorkatoAccountClient = null;
    this.existingWorkatoUserClient = null;
    this.importWorkatoRecipeClient = null;
    this.newWorkatoUserClient = null;
    this.newWorkatoAccountClient = null;
    this.workatoSettingsClient = null;
    this.workatoNewOnboardingPopupClient = null;
    this.workatoExistingOnboardingPopupClient = null;
  }

  async getWorkatoToken() {
    const workatoTokenSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };

    let workatoTokenResponse;
    try {
      if (!this.workatoTokenClient) {
        this.workatoTokenClient = createWorkatoTokenClient();
      }
      workatoTokenResponse = await this.sendClientRequest({
        client: this.workatoTokenClient,
        settings: workatoTokenSettings,
        errorMsg: 'Couldn\'t fetch workatoTokenResponse',
      });
      workatoTokenResponse = workatoTransformer.transformWorkatoTokenResponse(workatoTokenResponse);
    } catch (error) {
      workatoTokenResponse = Promise.reject(error);
      this.logError(`Error fetching workatoTokenResponse: ${error}`);
    }
    return workatoTokenResponse;
  }

  async getWorkatoRecipe() {
    const workatoRecipeSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };
    let workatoRecipeResponse;
    try {
      if (!this.workatoRecipeClient) {
        this.workatoRecipeClient = createWorkatoRecipeClient();
      }
      workatoRecipeResponse = await this.sendClientRequest({
        client: this.workatoRecipeClient,
        settings: workatoRecipeSettings,
        errorMsg: 'Couldn\'t fetch workatoRecipeResponse',
      });
      workatoRecipeResponse = workatoTransformer.transformWorkatoRecipeResponse(workatoRecipeResponse);
    } catch (error) {
      workatoRecipeResponse = Promise.reject(error);
      this.logError(`Error fetching workatoRecipeResponse: ${error}`);
    }
    return workatoRecipeResponse;
  }

  async getWorkatoAdmins() {
    const workatoAdminsSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };

    let workatoAdminsResponse;
    try {
      if (!this.workatoAdminsClient) {
        this.workatoAdminsClient = createWorkatoAdminsClient();
      }
      workatoAdminsResponse = await this.sendClientRequest({
        client: this.workatoAdminsClient,
        settings: workatoAdminsSettings,
        errorMsg: 'Couldn\'t fetch workatoAdminsResponse',
      });
      workatoAdminsResponse = workatoTransformer.transformWorkatoAdminsResponse(workatoAdminsResponse);
    } catch (error) {
      workatoAdminsResponse = Promise.reject(error);
      this.logError(`Error fetching workatoAdminsResponse: ${error}`);
    }
    return workatoAdminsResponse;
  }

  async getExistingWorkatoAccount() {
    const existingWorkatoAccountSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };

    let existingWorkatoAccountResponse;
    try {
      if (!this.existingWorkatoAccountClient) {
        this.existingWorkatoAccountClient = createExistingWorkatoAccountClient();
      }
      existingWorkatoAccountResponse = await this.sendClientRequest({
        client: this.existingWorkatoAccountClient,
        settings: existingWorkatoAccountSettings,
        errorMsg: 'Couldn\'t fetch existingWorkatoAccountResponse',
      });
      existingWorkatoAccountResponse = workatoTransformer.transformExistingWorkatoAccountResponse(existingWorkatoAccountResponse);
    } catch (error) {
      existingWorkatoAccountResponse = Promise.reject(error);
      this.logError(`Error fetching existingWorkatoAccountResponse: ${error}`);
    }
    return existingWorkatoAccountResponse;
  }

  async getExistingWorkatoUser() {
    const existingWorkatoUserSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };

    let existingWorkatoUserResponse;
    try {
      if (!this.existingWorkatoUserClient) {
        this.existingWorkatoUserClient = createExistingWorkatoUserClient();
      }
      existingWorkatoUserResponse = await this.sendClientRequest({
        client: this.existingWorkatoUserClient,
        settings: existingWorkatoUserSettings,
        errorMsg: 'Couldn\'t fetch existingWorkatoUserResponse',
      });
      existingWorkatoUserResponse = workatoTransformer.transformExistingWorkatoUserResponse(existingWorkatoUserResponse);
    } catch (error) {
      existingWorkatoUserResponse = Promise.reject(error);
      this.logError(`Error fetching existingWorkatoUserResponse: ${error}`);
    }
    return existingWorkatoUserResponse;
  }

  async importWorkatoRecipe() {
    const importWorkatoRecipeSettings = {
      method: WorkatoClient.HTTP_METHODS.PUT,
    };

    let importWorkatoRecipeResponse;
    try {
      if (!this.importWorkatoRecipeClient) {
        this.importWorkatoRecipeClient = importWorkatoRecipeClient();
      }
      importWorkatoRecipeResponse = await this.sendClientRequest({
        client: this.importWorkatoRecipeClient,
        settings: importWorkatoRecipeSettings,
        errorMsg: 'Couldn\'t fetch importWorkatoRecipe',
      });
      importWorkatoRecipeResponse = workatoTransformer.transformImportWorkatoRecipeResponse(importWorkatoRecipeResponse);
    } catch (error) {
      importWorkatoRecipeResponse = Promise.reject(error);
      this.logError(`Error fetching importWorkatoRecipeResponse: ${error}`);
    }
    return importWorkatoRecipeResponse;
  }

  async createNewWorkatoUser() {
    let newWorkatoUserResponse;
    try {
      if (!this.newWorkatoUserClient) {
        this.newWorkatoUserClient = createNewWorkatoUserClient();
      }
      const accountId = await store.getters['session/getValue']('accountId');
      const settings = {
        accountId,
      };
      const newWorkatoUserSettings = workatoTransformer.transformNewWorkatoUserRequest(settings);
      newWorkatoUserResponse = await this.sendClientRequest({
        client: this.newWorkatoUserClient,
        settings: newWorkatoUserSettings,
        errorMsg: 'Couldn\'t fetch newWorkatoUserResponse',
      });
      newWorkatoUserResponse = workatoTransformer.transformNewWorkatoUserResponse(newWorkatoUserResponse);
    } catch (error) {
      newWorkatoUserResponse = Promise.reject(error);
      this.logError(`Error fetching newWorkatoUserResponse: ${error}`);
    }
    return newWorkatoUserResponse;
  }

  async createNewWorkatoAccount() {
    let newWorkatoAccountResponse;
    try {
      if (!this.newWorkatoAccountClient) {
        this.newWorkatoAccountClient = createNewWorkatoAccountClient();
      }
      const accountId = await store.getters['session/getValue']('accountId');
      const settings = {
        accountId,
      };
      const newWorkatoAccountSettings = workatoTransformer.transformNewWorkatoAccountRequest(settings);
      newWorkatoAccountResponse = await this.sendClientRequest({
        client: this.newWorkatoAccountClient,
        settings: newWorkatoAccountSettings,
        errorMsg: 'Couldn\'t fetch newWorkatoAccountResponse',
      });
      newWorkatoAccountResponse = workatoTransformer.transformNewWorkatoAccountResponse(newWorkatoAccountResponse);
    } catch (error) {
      newWorkatoAccountResponse = Promise.reject(error);
      this.logError(`Error fetching newWorkatoAccountResponse: ${error}`);
    }
    return newWorkatoAccountResponse;
  }

  async fetchIhubConfig(ihubConfig) {
    const ihubConfigSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };

    let ihubConfigResponse;
    try {
      this.ihubConfigClient = createIhubConfigClient(ihubConfig);
      ihubConfigResponse = await this.sendClientRequest({
        client: this.ihubConfigClient,
        settings: ihubConfigSettings,
        errorMsg: 'Couldn\'t fetch ihubConfig',
      });
      ihubConfigResponse = workatoTransformer.transformIhubConfigResponse(ihubConfigResponse);
    } catch (error) {
      ihubConfigResponse = Promise.reject(error);
      this.logError(`Error fetching ihubConfigResponse: ${error}`);
    }
    return ihubConfigResponse;
  }

  async getWorkatoOnboardingPopup(configName = 'DO_NOT_SHOW_POP_UP') {
    const popupConfigSettings = {
      method: WorkatoClient.HTTP_METHODS.GET,
    };

    let workatoOnboardingPopupResponse;
    try {
      if (!this.workatoSettingsClient) {
        this.workatoSettingsClient = createWorkatoSettingsClient();
      }
      workatoOnboardingPopupResponse = await this.sendClientRequest({
        client: this.workatoSettingsClient,
        settings: popupConfigSettings,
        errorMsg: 'Couldn\'t fetch workatoOnboardingPopupResponse',
      });
      workatoOnboardingPopupResponse = workatoTransformer.transformWorkatoConfigsResponse(workatoOnboardingPopupResponse, configName);
    } catch (error) {
      workatoOnboardingPopupResponse = Promise.reject(error);
      this.logError(`Error fetching workatoOnboardingPopupResponse: ${error}`);
    }
    return workatoOnboardingPopupResponse;
  }

  async setWorkatoOnboardingPopup(configValue = false, createdFlag = true, configName = 'DO_NOT_SHOW_POP_UP') {
    const popupConfigSettings = workatoTransformer.transformOnboardingPopupRequest(
      {
        config_name: configName,
        config_value: configValue,
      },
      createdFlag,
    );
    let workatoOnboardingPopupResponse;
    try {
      const clientName = createdFlag ? 'workatoExistingOnboardingPopupClient' : 'workatoNewOnboardingPopupClient';
      if (!this.workatoNewOnboardingPopupClient && !createdFlag) {
        this.workatoNewOnboardingPopupClient = createNewWorkatoOnboardingPopupClient(configName);
      }
      if (!this.workatoExistingOnboardingPopupClient && createdFlag) {
        this.workatoExistingOnboardingPopupClient = createExistingWorkatoOnboardingPopupClient(configName);
      }
      workatoOnboardingPopupResponse = await this.sendClientRequest({
        client: this[clientName],
        settings: popupConfigSettings,
        errorMsg: 'Couldn\'t fetch workatoOnboardingPopupResponse',
      });
      workatoOnboardingPopupResponse = workatoTransformer.transformWorkatoConfigsResponse(workatoOnboardingPopupResponse, configName);
    } catch (error) {
      workatoOnboardingPopupResponse = Promise.reject(error);
      this.logError(`Error fetching workatoOnboardingPopupResponse: ${error}`);
    }
    return workatoOnboardingPopupResponse;
  }
}

function createWorkatoTokenClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.WORKATO_TOKEN,
    contextName: 'GetWorkatoToken',
  });
}

function createWorkatoRecipeClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.WORKATO_RECIPE,
    contextName: 'GetWorkatoRecipe',
  });
}

function createWorkatoAdminsClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.WORKATO_ADMINS,
    contextName: 'GetWorkatoAdmins',
  });
}

function createExistingWorkatoAccountClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.EXISTING_WORKATO_ACCOUNT,
    contextName: 'GetExistingWorkatoAccount',
  });
}

function createExistingWorkatoUserClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.EXISTING_WORKATO_USER,
    contextName: 'GetExistingWorkatoUser',
  });
}

function createNewWorkatoUserClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.NEW_WORKATO_USER,
    contextName: 'NewWorkatoUser',
  });
}

function createNewWorkatoAccountClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.NEW_WORKATO_ACCOUNT,
    contextName: 'NewWorkatoAccount',
  });
}

function createIhubConfigClient(ihubConfig) {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.IHUB_CONFIG,
    contextName: 'IhubConfig',
    ihubConfig,
  });
}

function importWorkatoRecipeClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.IMPORT_WORKATO_RECIPE,
    contextName: 'ImportWorkatoRecipe',
  });
}

function createWorkatoSettingsClient() {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.WORKATO_SETTINGS,
    contextName: 'WorkatoSetting',
  });
}

function createNewWorkatoOnboardingPopupClient(configName) {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.CREATE_WORKATO_ONBOARDING_POPUP_SETTING,
    contextName: 'WorkatoOnboardingPopup',
    configName,
  });
}

function createExistingWorkatoOnboardingPopupClient(configName) {
  return new WorkatoClient({
    resource: WorkatoClient.RESOURCES.UPDATE_WORKATO_ONBOARDING_POPUP_SETTING,
    contextName: 'WorkatoOnboardingPopup',
    configName,
  });
}

const workatoService = new WorkatoService();

export default workatoService;
